import React from "react";
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import YouTube from "react-youtube";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useScrolled } from "../../utils/Gsap/useScrolled";
import ShowSidebar from "./Sidebar/ShowSidebar";
import { Helmet } from "react-helmet";
import ShowAwardsSidebarContainer from "./Awards/components/ShowAwardsSidebarContainer";
import { getShowAwards } from "../../utils/api/helpers/show";
import { getShowDetailPage } from "../../utils/api/helpers/show";
import {
  getShowAlbums,
  getShowMediaByTag,
} from "../../utils/api/helpers/media";
import { S3URL, SHOW_MEDIA_SECTIONS } from "../../data/globalConstants";
import MediaSection from "./Media/MediaSection";
import PageLoader from "../../utils/Ui/PageLoader";
import { isMobile, isTablet } from "react-device-detect";
import AlbumSection from "./Media/AlbumSection";

import beefEventBanner from "../../assets/img/Beef_ReleaseYourRage_Banner.png";
import beefEventBannerMobile from "../../assets/img/Beef_ReleaseYourRage_Banner-Mobile.png";

gsap.registerPlugin(ScrollTrigger);

export default function ShowHome() {
  const { sectionSlug, genreSlug, showSlug } = useParams();
  const [isPageReady, setIsPageReady] = useState(false);

  const [showData, setShowData] = useState(null);
  const [showAwards, setShowAwards] = useState(null);
  const [hasAwards, setHasAwards] = useState(false);
  const [activeSection, setActiveSection] = useState(sectionSlug || "");
  const [isLoading, setIsLoading] = useState(true);
  const [mediaByTag, setMediaByTag] = React.useState({});
  const [showMediaAlbums, setShowMediaAlbums] = React.useState([]);
  const [availableSections, setAvailableSections] = React.useState([]);

  const containerRef = useRef(null);
  const bgImageRef = useRef();
  const showPageContentRef = useRef();
  const initialScrollDoneRef = useRef(false);
  const trailerRef = useRef(null);
  const navContainerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const scrolled = useScrolled(100);
  const scrollOffsetNumber = 150;

  const hasTrailer = Boolean(showData?.trailer_id);

  // Trigger build

  useEffect(() => {
    if (!isLoading && showData) {
      // Wait for next paint + layout
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsPageReady(true);
        });
      });
    }
  }, [isLoading, showData, availableSections]);

  useEffect(() => {
    if (scrolled && !initialScrollDoneRef.current) {
      initialScrollDoneRef.current = true;
    }
  }, [scrolled]);

  useEffect(() => {
    if (!sectionSlug && !initialScrollDoneRef.current) {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [sectionSlug, initialScrollDoneRef]);

  const vidOpts = {
    height: "390",
    width: "640",
    playerVars: {
      autoplay: 1,
      mute: 0,
      controls: 1,
      cc_load_policy: 0,
      fs: 1,
      iv_load_policy: 3,
      modestBranding: 1,
      rel: 0,
      showInfo: 0,
      playsInline: 1,
    },
  };

  useEffect(() => {
    if (!genreSlug || !showSlug) return;

    let isMounted = true;

    const loadShow = async () => {
      setIsLoading(true);

      try {
        const res = await getShowDetailPage(genreSlug, showSlug);
        const show = res?.data;

        const isShowDisabled =
          !show ||
          show.visible === false ||
          show.visible === 0 ||
          show.visible === "0";

        if (isShowDisabled) {
          navigate("/", { replace: true });
          return;
        }

        if (isMounted) {
          setShowData(show);
        }
      } catch (error) {
        console.error("Unable to load show:", error);
        navigate("/", { replace: true });
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadShow();

    return () => {
      isMounted = false;
    };
  }, [genreSlug, showSlug, navigate]);

  useEffect(() => {
    if (!genreSlug || !showSlug) return;

    getShowAwards(genreSlug, showSlug).then((res) => {
      if (res.data.competitions.length > 0) {
        setShowAwards(res.data.competitions);
        setHasAwards(true);
      }
    });
  }, [genreSlug, showSlug]);

  useEffect(() => {
    if (!genreSlug || !showSlug) return;

    const loadMedia = async () => {
      const CUSTOM_AUDIO_IFRAMES_BY_SHOW_SLUG = {
        "lord-of-the-flies": [
          {
            filename: null,
            type: "custom/iframe",
            title: "NPR Embedded Audio Player",
            description: "",
            private: 0,
            url: null,
            slug: "custom-npr-embed",
            media_id: "custom-npr-embed",
            thumbnail: null,
            all_tags: "audio",
            iframe: {
              src: "https://www.npr.org/player/embed/nx-s1-5753845/nx-s1-9755242",
              width: "100%",
              height: "290",
              frameBorder: "0",
              scrolling: "no",
              title: "NPR embedded audio player",
            },
          },
        ],
        "jeff-ross-take-a-banana-for-the-ride": [
          {
            filename: null,
            type: "custom/iframe",
            title: "Jeff Ross - Take a Banana for the Ride",
            description: "",
            private: 0,
            url: null,
            slug: "jeff-ross-take-a-banana-for-the-ride",
            media_id: "jeff-ross-take-a-banana-for-the-ride",
            thumbnail: null,
            all_tags: "audio",
            iframe: {
              src: "https://embed.podcasts.apple.com/us/podcast/jeff-ross/id1316200737?i=1000772170544",
              width: "100%",
              height: "175",
              frameBorder: "0",
              scrolling: "no",
              title: "Jeff Ross - Take a Banana for the Ride",
            },
          },
        ],
        "skyscraper-live": [
          {
            filename: null,
            type: "custom/iframe",
            title:
              "Creative Principles EP711 Alex Honnold - Free Soloist Climber",
            description: "",
            private: 0,
            url: null,
            slug: "alex-honnold-free-soloist-climber-free-solo",
            media_id: "alex-honnold-free-soloist-climber-free-solo",
            thumbnail: null,
            all_tags: "audio",
            iframe: {
              src: "https://embed.podcasts.apple.com/us/podcast/ep711-alex-honnold-free-soloist-climber-free-solo-and/id1292130289?i=1000773256215",
              width: "100%",
              height: "175",
              frameBorder: "0",
              scrolling: "no",
              title: "EP711 Alex Honnold - Free Soloist Climber",
            },
          },
        ],
        "wanda-sykes-legacy": [
          {
            filename: null,
            type: "custom/iframe",
            title: "Wanda Sykes - Legacy",
            description: "",
            private: 0,
            url: null,
            slug: "wanda-sykes-legacy",
            media_id: "wanda-sykes-legacy",
            thumbnail: null,
            all_tags: "audio",
            iframe: {
              src: "https://embed.podcasts.apple.com/us/podcast/wanda-sykes-wanda-sykes-legacy/id1039032256?i=1000772052898",
              width: "100%",
              height: "175",
              frameBorder: "0",
              scrolling: "no",
              title: "Wanda Sykes - Legacy",
            },
          },
        ],
      };

      // const CUSTOM_VIDEO_ITEMS_BY_SHOW_SLUG = {
      //   "something-very-bad-is-going-to-happen": [
      //     {
      //       filename: null,
      //       type: "custom/video",
      //       title:
      //         "Camila Morrone Talks Netflix's Something Very Bad Is Going To Happen",
      //       description: "",
      //       private: 0,
      //       url: null,
      //       slug: "camila-morrone-talks-netflixs-something-very-bad-is-going-to-happen",
      //       media_id: "custom-ktla-video-embed",
      //       thumbnail: "1780312068155-ktla.jpg",
      //       all_tags: "video",
      //     },
      //   ],
      // };

      const results = await Promise.allSettled(
        SHOW_MEDIA_SECTIONS.map((section) =>
          getShowMediaByTag(genreSlug, showSlug, section.key),
        ),
      );

      getShowAlbums(genreSlug, showSlug).then((result) => {
        if (
          result &&
          result.data &&
          result.status === 200 &&
          Object.keys(result.data).length > 0
        ) {
          // console.log("showMediaAlbums", result.data);
          setShowMediaAlbums(result.data);
        }
      });

      const dataMap = {};
      const available = [];

      results.forEach((result, i) => {
        const section = SHOW_MEDIA_SECTIONS[i];
        const tag = section.key;

        if (result.status === "fulfilled") {
          const data = Array.isArray(result.value?.data)
            ? result.value.data
            : [];

          // Reverse only the Creator Diaries section.
          const orderedData =
            tag === "creator-diaries" ? [...data].reverse() : data;

          dataMap[tag] = orderedData;

          if (orderedData.length > 0) {
            available.push(section);
          }
        } else {
          console.error(`Unable to load media tagged "${tag}":`, result.reason);

          dataMap[tag] = [];
        }
      });

      const customAudioItems =
        CUSTOM_AUDIO_IFRAMES_BY_SHOW_SLUG[showSlug] || [];

      if (customAudioItems.length > 0) {
        dataMap.audio = [...(dataMap.audio || []), ...customAudioItems];

        const audioSection = SHOW_MEDIA_SECTIONS.find(
          (section) => section.key === "audio",
        );

        const audioAlreadyAvailable = available.some(
          (section) => section.key === "audio",
        );

        if (audioSection && !audioAlreadyAvailable) {
          available.push(audioSection);
        }
      }

      // const customVideoItems =
      //   CUSTOM_VIDEO_ITEMS_BY_SHOW_SLUG[showSlug] || [];
      //
      // if (customVideoItems.length > 0) {
      //   dataMap.video = [
      //     ...(dataMap.video || []),
      //     ...customVideoItems,
      //   ];
      //
      //   const videoSection = SHOW_MEDIA_SECTIONS.find(
      //     (section) => section.key === "video",
      //   );
      //
      //   const videoAlreadyAvailable = available.some(
      //     (section) => section.key === "video",
      //   );
      //
      //   if (videoSection && !videoAlreadyAvailable) {
      //     available.push(videoSection);
      //   }
      // }

      setMediaByTag(dataMap);
      setAvailableSections(available);

      if (available.length > 0) {
        setActiveSection((prev) => prev || available[0].key);
      }
    };

    loadMedia();
  }, [genreSlug, showSlug]);

  // Keep activeSection in sync with route externally
  useEffect(() => {
    if (sectionSlug && sectionSlug !== activeSection) {
      setActiveSection(sectionSlug);
    }
  }, [sectionSlug, activeSection]);

  const replaceShowURL = (section = "") => {
    const path = section
      ? `/${genreSlug}/${showSlug}/${section}`
      : `/${genreSlug}/${showSlug}`;

    window.history.replaceState(window.history.state, "", path);
  };

  const handleNavClick = (targetSlug, e) => {
    e.preventDefault();

    replaceShowURL(targetSlug);
    setActiveSection(targetSlug);

    const el = document.getElementById(targetSlug);

    if (el) {
      const offset = scrollOffsetNumber;
      const targetPosition =
        el.getBoundingClientRect().top + window.scrollY - offset;

      gsap.to(window, {
        scrollTo: targetPosition,
        duration: 0.2,
        ease: "none",
      });
    }
  };

  const handleBackToTop = () => {
    gsap.to(window, {
      scrollTo: 0,
      duration: 0.8,
      ease: "power2.out",
    });

    replaceShowURL();
    setActiveSection("");
  };

  const scrollToSectionSafe = (
    id,
    offset = scrollOffsetNumber,
    attempts = 0,
  ) => {
    if (initialScrollDoneRef.current) return;

    const el = document.getElementById(id);

    // Element not found yet → retry
    if (!el) {
      if (attempts < 10) {
        requestAnimationFrame(() =>
          scrollToSectionSafe(id, offset, attempts + 1),
        );
      } else {
        initialScrollDoneRef.current = true;
        setActiveSection("");
        ScrollTrigger.refresh(true);
      }

      return;
    }

    // Element found → scroll
    const targetPosition =
      el.getBoundingClientRect().top + window.scrollY - offset;

    gsap.to(window, {
      scrollTo: targetPosition,
      duration: 0.4,
      ease: "power2.out",
      onComplete: () => {
        initialScrollDoneRef.current = true;
        setActiveSection(id);
        ScrollTrigger.refresh(true);
      },
    });
  };

  useGSAP(() => {
    if (!isPageReady) return;

    const ctx = gsap.context(() => {
      const navEl = navContainerRef.current;

      if (navEl) {
        // If your CSS sticky is like: top: 0 or top: 20px, match that here.
        const stickyTop = 70;

        ScrollTrigger.create({
          trigger: navEl,
          start: `top top+=${stickyTop}`,
          end: 999999,
          onToggle: (self) => {
            navEl.classList.toggle("is-stuck", self.isActive);
          },
          // markers: true,
        });
      }

      const bgImg = bgImageRef.current;
      const showPage = showPageContentRef.current;
      const initialYPosition =
        document.querySelector(".show-page-inner").getBoundingClientRect().top +
        window.scrollY;

      // --- BG image entrance ---
      if (bgImg) {
        const bgTl = gsap.timeline({
          defaults: { duration: 1.5, ease: "power3.inOut" },
          onComplete: () => {
            // After entrance, initialize blur ScrollTrigger
            ScrollTrigger.create({
              trigger: ".show-page-inner",
              start: `top ${initialYPosition}`,
              end: "top 175px",
              scrub: true,
              // markers: true,
              onUpdate: (self) => {
                const progress = self.progress;

                gsap.to(bgImg, {
                  duration: 1,
                  filter: `blur(${5 * progress}px)`,
                  opacity: 1 - 0.5 * progress,
                });
              },
            });

            // --- Section blur/darken ---
            gsap.utils.toArray(".show-page-section").forEach((el) => {
              ScrollTrigger.create({
                trigger: el,
                start: "top top",
                end: "bottom 75px",
                scrub: true,
                // markers: true,
                onUpdate: (self) => {
                  const progress = self.progress;

                  gsap.set(el, {
                    // filter: `blur(${20 * progress}px) brightness(${1 - 0.1 * progress})`,
                    // filter: `brightness(${1 - 0.1 * progress})`,
                    opacity: 1 - 0.9 * progress,
                  });
                },
              });
            });

            // Perform initial scroll
            if (!initialScrollDoneRef.current) {
              if (sectionSlug) {
                scrollToSectionSafe(sectionSlug);
              } else if (!isMobile && !isTablet && hasTrailer) {
                scrollToSectionSafe("trailer");
              }
            }
          },
        });

        if (sectionSlug) {
          gsap.set(bgImg, {
            opacity: 0.5,
            filter: "blur(5px)",
          });
        } else {
          gsap.set(bgImg, {
            opacity: 0,
            filter: "blur(5px)",
          });

          bgTl.to(
            bgImg,
            {
              opacity: 1,
              filter: "blur(0px)",
            },
            0,
          );
        }

        gsap.set(showPage, { opacity: 0 });
        bgTl.to(showPage, { opacity: 1 }, 0.5);
      }

      // Collect ScrollTriggers so we can disable them temporarily
      const triggers = [];

      gsap.utils.toArray(".show-page-section").forEach((element, index) => {
        const st = ScrollTrigger.create({
          trigger: element,
          start: "top center",
          end: "bottom center",
          scrub: true,
          onEnter: (e) => {
            if (!initialScrollDoneRef.current) return;

            replaceShowURL(e.trigger.id);
            setActiveSection(e.trigger.id);
          },

          onEnterBack: (e) => {
            if (!initialScrollDoneRef.current) return;

            replaceShowURL(e.trigger.id);
            setActiveSection(e.trigger.id);
          },

          onLeaveBack: () => {
            if (!initialScrollDoneRef.current) return;

            if (index === 0) {
              replaceShowURL();
              setActiveSection("");
            }
          },
        });

        triggers.push(st);
      });
    }, containerRef);

    return () => ctx.revert();
  }, [isPageReady, hasTrailer]);

  useEffect(() => {
    const grids = document.querySelectorAll(".show-page-section-grid");

    const cleanups = [];

    grids.forEach((grid) => {
      const items = grid.querySelectorAll(".show-page-section-item");

      items.forEach((item) => {
        const handleMouseEnter = () => {
          items.forEach((other) => {
            if (other !== item) {
              other.classList.add("dimmed");
            }
          });
        };

        const handleMouseLeave = () => {
          items.forEach((other) => {
            other.classList.remove("dimmed");
          });
        };

        item.addEventListener("mouseenter", handleMouseEnter);
        item.addEventListener("mouseleave", handleMouseLeave);

        cleanups.push(() => {
          item.removeEventListener("mouseenter", handleMouseEnter);
          item.removeEventListener("mouseleave", handleMouseLeave);
        });
      });
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [mediaByTag]);

  useEffect(() => {
    const stopEmbeddedMedia = () => {
      const player = trailerRef.current?.internalPlayer;

      player?.pauseVideo?.();
      player?.stopVideo?.();
    };

    window.addEventListener("stop-embedded-media", stopEmbeddedMedia);

    return () => {
      window.removeEventListener("stop-embedded-media", stopEmbeddedMedia);
    };
  }, []);

  return (
    <>
      <PageLoader isLoading={isLoading} />

      <div ref={containerRef}>
        {showData && showData?.title && (
          <Helmet>
            <title>{`${showData?.title} | Netflix Series FYC`}</title>
            <meta name="description" content={`${showData?.title}`} />
          </Helmet>
        )}

        <div className="width-ctrl show-page-container">
          <div className="show-page-inner" ref={showPageContentRef}>
            <div
              className="show-left-column"
              style={{
                top: !hasAwards ? "33%" : "",
                height: !hasAwards ? "fit-content" : "",
              }}
            >
              <ShowSidebar
                showData={showData}
                genreSlug={genreSlug}
                showSlug={showSlug}
                displayAwards={hasAwards}
                showAwards={showAwards}
              />
            </div>

            <div className="show-right-column">
              <div className="show-nav-container" ref={navContainerRef}>
                <nav className="show-nav" aria-label="Show navigation">
                  <button
                    type="button"
                    style={{ opacity: scrolled ? 1 : 0 }}
                    className="show-nav-button return-to-top"
                    onClick={handleBackToTop}
                    aria-label="Return to top of page"
                  >
                    <img
                      src="/img/ico/arrow-up.png"
                      alt=""
                      aria-hidden="true"
                    />

                    <span>Top</span>
                  </button>

                  {hasTrailer && (
                    <button
                      type="button"
                      className={`show-nav-button ${
                        activeSection === "trailer" ? "active" : ""
                      }`}
                      onClick={(e) => handleNavClick("trailer", e)}
                      aria-label="Scroll to trailer section"
                    >
                      <span>Trailer</span>
                    </button>
                  )}

                  {availableSections.map((section) => (
                    <button
                      key={section.key}
                      type="button"
                      className={`show-nav-button ${
                        activeSection === section.key ? "active" : ""
                      }`}
                      onClick={(e) => handleNavClick(section.key, e)}
                      aria-label={`Scroll to ${
                        section.label.charAt(0).toUpperCase() +
                        section.label.slice(1)
                      } section`}
                    >
                      <span>
                        {section.label.charAt(0).toUpperCase() +
                          section.label.slice(1)}
                      </span>
                    </button>
                  ))}

                  {showMediaAlbums.length > 0 && (
                    <button
                      type="button"
                      className={`show-nav-button ${
                        activeSection === "photos" ? "active" : ""
                      }`}
                      onClick={(e) => handleNavClick("photos", e)}
                      aria-label="Scroll to photos section"
                    >
                      <span>Photos</span>
                    </button>
                  )}
                </nav>
              </div>

              <div className="show-main-content">
                {hasTrailer ? (
                  <section
                    id="trailer"
                    className="show-page-section trailer-section"
                  >
                    <div className="trailer-container">
                      <YouTube
                        videoId={showData?.trailer_id}
                        opts={vidOpts}
                        ref={trailerRef}
                      />
                    </div>
                  </section>
                ) : (
                  <></>
                )}

                {genreSlug === "limited-series-movie" &&
                  showSlug === "beef" && (
                    <div className="show-page-banner mb-5">
                      <a href={"/beef-release-your-rage"}>
                        <img
                          src={beefEventBanner}
                          alt="BEEF: Release your rage. Feel your calm."
                          aria-hidden="true"
                          className="show-page-banner-image d-none d-sm-block"
                        />
                        <img
                          src={beefEventBannerMobile}
                          alt="BEEF: Release your rage. Feel your calm."
                          aria-hidden="true"
                          className="show-page-banner-image d-block d-sm-none"
                        />
                      </a>
                    </div>
                  )}

                {SHOW_MEDIA_SECTIONS.filter(
                  (section) => (mediaByTag[section.key] || []).length > 0,
                ).map((section) => (
                  <MediaSection
                    key={section.key}
                    slug={section.key}
                    title={section.label}
                    items={mediaByTag[section.key]}
                  />
                ))}

                {showMediaAlbums.length > 0 && (
                  <section id="photos" className="show-page-section">
                    <h3 className="show-page-section-title">Photos</h3>

                    {showMediaAlbums.map((album, index) => (
                      <React.Fragment key={album?.slug}>
                        {index > 0 && (
                          <div
                            style={{
                              borderTop: "1px solid rgba(255,255,255,.1)",
                              height: "1px",
                            }}
                            className="my-4"
                          />
                        )}

                        <AlbumSection
                          slug={album?.slug}
                          title={album?.title}
                          items={album?.media}
                        />
                      </React.Fragment>
                    ))}
                  </section>
                )}

                {hasAwards && (
                  <div className="d-block d-sm-none">
                    <ShowAwardsSidebarContainer
                      genreSlug={genreSlug}
                      showSlug={showSlug}
                      showAwards={showAwards}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="show-page-background" ref={bgImageRef}>
          <img
            src={S3URL + showData?.backgroundImage}
            loading="lazy"
            alt={showData?.title}
            className="show-masked-image"
          />
        </div>
      </div>
    </>
  );
}
